/**
 * What each person is told, how, and how far ahead.
 *
 * One card per group, the way the list reads out loud: your schedule, your documents,
 * the equipment you hold, and — only for someone who can see those sections — what the
 * office needs to know. Each row is a switch; switching one on opens its two choices
 * underneath (how it reaches you, and when), so a page of fourteen types stays a page
 * of fourteen lines until someone wants to change one.
 *
 * Nothing saves until "Save preferences": a row of chips that saved on every tap would
 * send a dozen requests for one change of mind, and leave half of them applied if the
 * connection dropped.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Switch } from '@/components/Switch';
import { Card, ErrorNotice, Spinner } from '@/components/ui';
import { useI18n, type TranslationKey } from '@/i18n';
import {
  api,
  ApiError,
  type NotificationChannel,
  type NotificationGroup,
  type NotificationSettings as Settings,
  type NotificationTypePreference,
} from '@/lib/api';

import { leadLabel } from './format';
import * as webPush from './webPush';

interface Choice {
  enabled: boolean;
  channels: NotificationChannel[];
  leadTimes: number[];
}

const GROUPS: NotificationGroup[] = ['schedule', 'documents', 'equipment', 'office'];
/**
 * The two a person chooses between. In-app is not offered as a choice: every
 * notification lands in the bell regardless, so a switch for it could only mislead.
 */
const CHANNELS: NotificationChannel[] = ['push', 'email'];

function fromServer(types: NotificationTypePreference[]): Record<string, Choice> {
  return Object.fromEntries(
    types.map((t) => [
      t.key,
      {
        enabled: t.enabled,
        channels: [...t.selectedChannels],
        leadTimes: [...t.leadTimes],
      },
    ]),
  );
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function chip(active: boolean, locked = false): string {
  return [
    'h-8 px-3 inline-flex items-center gap-1.5 rounded-full border text-[13px] font-medium transition-colors',
    active
      ? 'bg-[var(--color-primary-soft)] text-primary border-[var(--color-border-strong)] font-semibold'
      : 'bg-surface text-muted border-border hover:text-text hover:bg-surface-alt',
    locked ? 'cursor-default' : '',
  ].join(' ');
}

function Check() {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="w-3.5 h-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m3.5 8.5 3 3 6-7" />
    </svg>
  );
}

export function NotificationSettings() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const prefs = useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: api.notificationPreferences,
  });

  const [choices, setChoices] = useState<Record<string, Choice> | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!prefs.data) return;
    setChoices(fromServer(prefs.data.types));
    setSettings({ ...prefs.data.settings });
  }, [prefs.data]);

  const original = useMemo(() => (prefs.data ? fromServer(prefs.data.types) : null), [prefs.data]);
  const changedTypes = useMemo(() => {
    if (!choices || !original) return [];
    return Object.keys(choices).filter((k) => !same(choices[k], original[k]));
  }, [choices, original]);
  const settingsChanged = Boolean(settings && prefs.data && !same(settings, prefs.data.settings));
  const dirty = changedTypes.length > 0 || settingsChanged;

  const save = useMutation({
    mutationFn: () =>
      api.saveNotificationPreferences({
        preferences: Object.fromEntries(changedTypes.map((k) => [k, choices![k]!])),
        ...(settingsChanged ? { settings: settings! } : {}),
      }),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({
        queryKey: ['notificationPreferences'],
      });
      setTimeout(() => setSaved(false), 4000);
    },
  });

  if (prefs.isLoading || !choices || !settings) return <Spinner />;
  if (prefs.error) return <ErrorNotice error={prefs.error} />;
  const data = prefs.data!;

  const picks = (c: NotificationChannel) =>
    Object.values(choices).some((x) => x.enabled && x.channels.includes(c));
  const waiting: TranslationKey[] = [
    ...(picks('push') && !data.available.push ? (['notifications.pushMissing'] as const) : []),
    ...(picks('email') && !data.available.email ? (['notifications.emailPending'] as const) : []),
  ];

  const update = (key: string, next: Partial<Choice>) =>
    setChoices((current) => (current ? { ...current, [key]: { ...current[key]!, ...next } } : current));

  return (
    <div className="max-w-3xl space-y-6">
      <p className="text-sm text-muted">{t('notifications.intro')}</p>

      {data.available.webPush ? <ThisDevice /> : null}

      {/* Said once, here, rather than under every row that picks the channel. */}
      {waiting.length > 0 ? (
        <div className="rounded-md border border-border bg-surface-alt px-4 py-3 space-y-1">
          {waiting.map((key) => (
            <p key={key} className="text-xs text-muted">
              {t(key)}
            </p>
          ))}
        </div>
      ) : null}

      {GROUPS.map((group) => {
        const types = data.types.filter((x) => x.group === group);
        if (types.length === 0) return null;
        return (
          <Card key={group}>
            <h2 className="font-bold mb-2">{t(`notifications.groups.${group}` as TranslationKey)}</h2>
            <div className="divide-y divide-border border-t border-border">
              {types.map((type) => (
                <TypeRow
                  key={type.key}
                  type={type}
                  choice={choices[type.key]!}
                  onChange={(next) => update(type.key, next)}
                />
              ))}
            </div>
          </Card>
        );
      })}

      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-bold mb-1">{t('notifications.quiet')}</h2>
            <p className="text-xs text-muted">{t('notifications.quietHint')}</p>
          </div>
          <Switch
            checked={settings.quietEnabled}
            label={t('notifications.quiet')}
            onChange={(quietEnabled) => setSettings({ ...settings, quietEnabled })}
          />
        </div>
        {settings.quietEnabled ? (
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="block text-xs font-semibold text-muted mb-1">{t('notifications.from')}</span>
              <input
                type="time"
                className="input tabular w-32"
                value={settings.quietFrom}
                onChange={(e) => e.target.value && setSettings({ ...settings, quietFrom: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-muted mb-1">{t('notifications.to')}</span>
              <input
                type="time"
                className="input tabular w-32"
                value={settings.quietTo}
                onChange={(e) => e.target.value && setSettings({ ...settings, quietTo: e.target.value })}
              />
            </label>
          </div>
        ) : null}

        <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">{t('notifications.emailLanguage')}</div>
            <div className="text-xs text-muted">{t('notifications.emailLanguageHint')}</div>
          </div>
          <div className="flex gap-2" role="group" aria-label={t('notifications.emailLanguage')}>
            {(['it', 'en', 'ar'] as const).map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={settings.locale === l}
                className={chip(settings.locale === l)}
                onClick={() => setSettings({ ...settings, locale: l })}
              >
                {t(`locale.${l}` as TranslationKey)}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {save.error ? (
        <p className="text-sm text-danger">
          {save.error instanceof ApiError ? save.error.message : t('common.saveError')}
        </p>
      ) : null}

      {/* Only there when there is something to save (or it has just been saved): on a
          phone a permanently docked, greyed-out button eats a fifth of the screen. */}
      {dirty || saved ? (
        <div className="sticky bottom-0 z-20 -mx-1 flex items-center gap-3 border-t border-border bg-surface px-1 py-2.5">
          <button
            type="button"
            className="btn-primary"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.saving') : t('notifications.save')}
          </button>
          {dirty ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setChoices(fromServer(data.types));
                setSettings({ ...data.settings });
              }}
            >
              {t('notifications.discard')}
            </button>
          ) : null}
          {saved && !dirty ? (
            <span className="text-sm font-semibold text-success">{t('notifications.saved')}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function TypeRow({
  type,
  choice,
  onChange,
}: {
  type: NotificationTypePreference;
  choice: Choice;
  onChange: (next: Partial<Choice>) => void;
}) {
  const { t } = useI18n();
  const title = t(`notifications.types.${type.key}.title` as TranslationKey);
  const channels = CHANNELS.filter((c) => type.channels.includes(c));

  const toggleChannel = (c: NotificationChannel) => {
    if (c === 'in_app') return;
    const has = choice.channels.includes(c);
    onChange({
      channels: has ? choice.channels.filter((x) => x !== c) : [...choice.channels, c],
    });
  };

  const toggleLead = (value: number) => {
    const timing = type.timing!;
    if (!timing.multiple) return onChange({ leadTimes: [value] });
    const has = choice.leadTimes.includes(value);
    const next = has ? choice.leadTimes.filter((x) => x !== value) : [...choice.leadTimes, value];
    // At least one: an enabled reminder with no moment would simply never arrive.
    if (next.length === 0) return;
    onChange({ leadTimes: next.sort((a, b) => a - b) });
  };


  return (
    <div className="py-3.5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-muted leading-snug mt-0.5">
            {t(`notifications.types.${type.key}.hint` as TranslationKey)}
          </div>
        </div>
        <Switch checked={choice.enabled} label={title} onChange={(enabled) => onChange({ enabled })} />
      </div>

      {choice.enabled ? (
        <div className="mt-3 space-y-3 rounded-md bg-surface-alt border border-border px-3 py-3">
          <div>
            <div className="label-text mb-1.5">{t('notifications.how')}</div>
            <div className="flex flex-wrap gap-2">
              {channels.map((c) => {
                const on = choice.channels.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={on}
                    className={chip(on)}
                    onClick={() => toggleChannel(c)}
                  >
                    {on ? <Check /> : null}
                    {t(`notifications.channels.${c}` as TranslationKey)}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-subtle">
              {t(
                channels.some((c) => choice.channels.includes(c))
                  ? 'notifications.alsoInBell'
                  : 'notifications.onlyInBell',
              )}
            </p>
          </div>

          {type.timing ? (
            <div>
              <div className="label-text mb-1.5">
                {t(`notifications.timing.${type.key}` as TranslationKey)}
                {type.timing.multiple ? (
                  <span className="ms-2 normal-case tracking-normal font-normal">{t('notifications.multiple')}</span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {type.timing.options.map((value) => {
                  const on = choice.leadTimes.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={on}
                      className={chip(on)}
                      onClick={() => toggleLead(value)}
                    >
                      {on ? <Check /> : null}
                      {leadLabel(t, value, type.timing!.unit)}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Push for the browser this page is open in. Separate from the per-type choices: those
 * say which notifications should go by push; this says whether this device is one of
 * the places push goes to.
 */
function ThisDevice() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [state, setState] = useState<webPush.PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    webPush.status().then(setState).catch(() => setState('unsupported'));
  }, []);

  if (!state) return null;

  const run = async (action: () => Promise<webPush.PushStatus>) => {
    setBusy(true);
    setFailed(false);
    try {
      setState(await action());
      await queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const text: Record<webPush.PushStatus, TranslationKey> = {
    on: 'notifications.device.on',
    off: 'notifications.device.off',
    denied: 'notifications.device.denied',
    'install-first': 'notifications.device.installFirst',
    unsupported: 'notifications.device.unsupported',
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={`inline-block w-2 h-2 rounded-full ${state === 'on' ? 'bg-success' : 'bg-border'}`}
            />
            <h2 className="font-bold text-sm">{t('notifications.device.title')}</h2>
          </div>
          <p className="text-xs text-muted mt-1">{t(text[state])}</p>
          {failed ? <p className="text-xs text-danger mt-1">{t('notifications.device.failed')}</p> : null}
        </div>
        {state === 'off' ? (
          <button type="button" className="btn-primary" disabled={busy} onClick={() => run(webPush.turnOn)}>
            {t('notifications.device.turnOn')}
          </button>
        ) : null}
        {state === 'on' ? (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => run(webPush.turnOff)}>
            {t('notifications.device.turnOff')}
          </button>
        ) : null}
      </div>
    </Card>
  );
}
